import { useState } from "react";
import { FiLock, FiCheck, FiAlertCircle } from "react-icons/fi";
import API_URL from "../config";

export default function MyPin({ token, user }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const [managerPin, setManagerPin] = useState("");
  const [managerConfirm, setManagerConfirm] = useState("");
  const [managerLoading, setManagerLoading] = useState(false);
  const [managerSuccess, setManagerSuccess] = useState("");
  const [managerError, setManagerError] = useState("");

  const isPrivileged = user?.role === "OWNER" || user?.role === "MANAGER";
  const requiredLen = isPrivileged ? 6 : 4;
  const placeholder = "•".repeat(requiredLen);
  const hint = isPrivileged ? "6-digit" : "4-digit";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const re = isPrivileged ? /^\d{6}$/ : /^\d{4}$/;
    if (!re.test(pin)) {
      setError(`PIN must be exactly ${requiredLen} digits.`);
      return;
    }
    if (pin !== confirm) {
      setError("PINs do not match.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/my-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message);
        setPin("");
        setConfirm("");
      } else {
        setError(data.error || "Failed to set PIN.");
      }
    } catch {
      setError("Connection error.");
    } finally {
      setLoading(false);
    }
  };

  const handleManagerPinSubmit = async (e) => {
    e.preventDefault();
    setManagerError("");
    setManagerSuccess("");
    if (!/^\d{6}$/.test(managerPin)) {
      setManagerError("Discount Approval PIN must be exactly 6 digits.");
      return;
    }
    if (managerPin !== managerConfirm) {
      setManagerError("PINs do not match.");
      return;
    }
    setManagerLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/my-manager-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pin: managerPin }),
      });
      const data = await res.json();
      if (res.ok) {
        setManagerSuccess(data.message);
        setManagerPin("");
        setManagerConfirm("");
      } else {
        setManagerError(data.error || "Failed to set PIN.");
      }
    } catch {
      setManagerError("Connection error.");
    } finally {
      setManagerLoading(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-200 flex items-center justify-center">
          <FiLock size={18} className="text-orange-500" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">My Kiosk PIN</h2>
          <p className="text-slate-500 text-sm">Set your PIN to clock in/out on the kiosk</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <p className="text-slate-600 text-sm mb-5">
          Hi <strong>{user?.name}</strong>. Set your {hint} kiosk PIN below.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="inv-label">New PIN</label>
            <input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={e => { setPin(e.target.value); setError(""); setSuccess(""); }}
              placeholder={placeholder}
              maxLength={requiredLen}
              required
              className="inv-input font-mono tracking-widest focus:border-orange-500"
            />
          </div>
          <div>
            <label className="inv-label">Confirm PIN</label>
            <input
              type="password"
              inputMode="numeric"
              value={confirm}
              onChange={e => { setConfirm(e.target.value); setError(""); setSuccess(""); }}
              placeholder={placeholder}
              maxLength={requiredLen}
              required
              className="inv-input font-mono tracking-widest focus:border-orange-500"
            />
          </div>

          {error && (
            <div className="inv-error">
              <FiAlertCircle size={15} className="text-red-500 flex-shrink-0" />
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <FiCheck size={15} className="text-green-600 flex-shrink-0" />
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !pin || !confirm}
            className="w-full py-2.5 bg-orange-500 hover:bg-orange-400 text-white font-semibold rounded-lg text-sm transition-colors disabled:opacity-40"
          >
            {loading ? "Saving…" : "Set PIN"}
          </button>
        </form>
      </div>

      <p className="text-slate-400 text-xs text-center mt-4">
        After saving, go to the kiosk and enter this {hint} PIN to clock in.
      </p>

      {isPrivileged && (
        <div className="mt-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center">
              <FiLock size={18} className="text-blue-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Discount Approval PIN</h2>
              <p className="text-slate-500 text-sm">Used to approve discounts at the POS</p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <p className="text-slate-600 text-sm mb-5">
              Enter a 6-digit PIN. Must be different from your kiosk PIN.
            </p>

            <form onSubmit={handleManagerPinSubmit} className="space-y-4">
              <div>
                <label className="inv-label">New Discount PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={managerPin}
                  onChange={e => { setManagerPin(e.target.value); setManagerError(""); setManagerSuccess(""); }}
                  placeholder="••••••"
                  maxLength={6}
                  required
                  className="inv-input font-mono tracking-widest focus:border-blue-500"
                />
              </div>
              <div>
                <label className="inv-label">Confirm Discount PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={managerConfirm}
                  onChange={e => { setManagerConfirm(e.target.value); setManagerError(""); setManagerSuccess(""); }}
                  placeholder="••••••"
                  maxLength={6}
                  required
                  className="inv-input font-mono tracking-widest focus:border-blue-500"
                />
              </div>

              {managerError && (
                <div className="inv-error">
                  <FiAlertCircle size={15} className="text-red-500 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{managerError}</p>
                </div>
              )}

              {managerSuccess && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <FiCheck size={15} className="text-green-600 flex-shrink-0" />
                  <p className="text-green-700 text-sm">{managerSuccess}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={managerLoading || !managerPin || !managerConfirm}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm transition-colors disabled:opacity-40"
              >
                {managerLoading ? "Saving…" : "Set Discount PIN"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
